import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Modal } from '@mantine/core';
import type { GoogleDriveFile } from '../../../../api/google-integrations-client/google-integrations-client.type';
import { isSafeUrl } from '../../../../helpers/general-helpers';
import { getGoogleFilePreview } from './google-file-preview.helpers';

interface GoogleFilePreviewModalProps {
  /** The file to preview, or null when the modal is closed. */
  file: GoogleDriveFile | null;
  /** Called when the modal requests to close. */
  onClose: () => void;
}

/**
 * Modal that shows a read-only inline preview of a Google Drive file plus an
 * "Open in Google" escape hatch. The preview iframe authenticates via the
 * viewer's own browser Google session, so it only renders for files the current
 * user can open; the open-in-Google link covers the cases it cannot show.
 *
 * @param {GoogleFilePreviewModalProps} props - The file to preview and close handler.
 * @returns {JSX.Element} The preview modal.
 */
export function GoogleFilePreviewModal({ file, onClose }: GoogleFilePreviewModalProps) {
  const preview = file ? getGoogleFilePreview(file) : null;

  return (
    // Compound Modal API: Modal.Content is the [role="dialog"] element and
    // Mantine points its aria-labelledby at Modal.Title, so the visible file
    // name doubles as the dialog's accessible name (Modal's plain `aria-label`
    // lands on the root wrapper, not the dialog, so it would not name it).
    // xOffset 0: the app sizes boxes as content-box, so Mantine's default 5vw
    // side padding would push a wide modal past the right edge.
    <Modal.Root opened={file !== null} onClose={onClose} centered radius="lg" size="90%" padding={0} xOffset={0}>
      <Modal.Overlay />
      <Modal.Content>
        <ContentLayout>
          {file && preview && (
            <>
              <Header>
                <HeaderLeft>
                  <Icon icon={preview.icon} width={18} height={18} />
                  <Title title={file.name}>{file.name}</Title>
                </HeaderLeft>
                <HeaderRight>
                  {isSafeUrl(file.webViewLink) && (
                    <OpenLink href={file.webViewLink} target="_blank" rel="noopener noreferrer">
                      <Icon icon="material-symbols:open-in-new" width={16} height={16} />
                      Open in Google
                    </OpenLink>
                  )}
                  <CloseButton type="button" onClick={onClose} aria-label="Close preview">
                    <Icon icon="material-symbols:close" width={18} height={18} />
                  </CloseButton>
                </HeaderRight>
              </Header>
              <PreviewFrame
                src={preview.previewUrl}
                title={`${preview.label} preview: ${file.name}`}
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              />
            </>
          )}
        </ContentLayout>
      </Modal.Content>
    </Modal.Root>
  );
}

// Styled as a plain inner wrapper, not via styled(Modal.Content): Mantine also
// applies a Modal.Content className to the modal's flex "inner" wrapper, which
// turned that wrapper into a column and collapsed the 95% width to the
// content's own width.
const ContentLayout = styled.div`
  height: 85vh;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--sky-light);
  color: var(--ink-dark);
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

// Rendered as Modal.Title so Modal.Content's aria-labelledby names the dialog.
const Title = styled(Modal.Title)`
  font-weight: 700;
  color: var(--ink-darkest);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const OpenLink = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-dark);
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ink-lighter);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--sky-light);
    color: var(--ink-darkest);
  }
`;

const PreviewFrame = styled.iframe`
  display: block;
  width: 100%;
  flex: 1;
  min-height: 0;
  border: none;
`;
